import { on } from "events";
import { OpenToFightCategories, OrganizationRole } from "~/types/enums";
import type { GymsDTO, GymWithMembers } from "~/types/gyms.types";
import type { PlayerUIModel } from "~/types/players.types";
import type { Tables } from "~/types/types.gen.ts";

export const useGymStore = defineStore("gymStore", {
  state: () => ({
    gyms: [] as GymWithMembers[],
    gymsForUser: [] as GymWithMembers[],
    gymsFetched: false,
    gymFetchInProgress: true,
  }),
  getters: {
    isUserMemberOfGym: (state) => {
      return (gymId: string): boolean => {
        const user = useUserStore().user;
        if (!user) {
          return false;
        }

        const gym = state.gymsForUser.find((gym) => gym.id === gymId);
        if (!gym || !gym.members) {
          return false;
        }

        return gym.members.some((member) => member.user_id === user.id);
      };
    },
  },
  actions: {
    async getGymsForCurrentUser(): Promise<GymWithMembers[] | null> {
      if (
        !useUserStore().isUserLoggedIn ||
        !useUserStore().isGymOwner ||
        this.gymsForUser.length > 0
      ) {
        this.gymFetchInProgress = false;
        return this.gymsForUser;
      }

      this.gymFetchInProgress = true;

      const { data, error } = await usePrivateApiFetch()<GymsDTO>("/api/gyms", {
        method: HttpMethod.GET,
        query: {
          sorting: "name+asc",
          user_id: useUserStore().user?.id,
          members: true,
          fighters: true,
        },
      });

      this.gymFetchInProgress = false;

      if (error || !data) {
        console.error("Error fetching gyms for user:", error);

        usePosthog().posthogLogError({
          error,
          context: {
            userId: useUserStore().user?.id,
            action: "getGymsForCurrentUser",
          },
        });

        return null;
      }

      this.gymsForUser = data ?? [];

      return this.gymsForUser;
    },

    async addGym(gymData: Partial<GymWithMembers>) {
      const { data, error: createError } = await useFetch("/api/gyms", {
        method: HttpMethod.POST,
        body: gymData,
      });

      const error = data.value?.error || createError.value;
      if (error) {
        usePosthog().posthogLogError({
          message: "Failed to create gym",
          error,
          context: {
            userId: useUserStore().user?.id,
            action: "addGym",
          },
        });

        throw error;
      }

      const newGym = data.value?.data as GymWithMembers;

      if (newGym) {
        this.gymsForUser.push(newGym);
      }

      this.sortGymsByName();
    },

    async editGym(
      updatedData: Partial<Tables<"gyms">>,
      options?: { confirmVerificationInvalidation?: boolean },
    ): Promise<GymWithMembers> {
      const { data, error: updateError } = await useFetch(`/api/gyms`, {
        method: HttpMethod.PUT,
        body: {
          ...updatedData,
          ...(options?.confirmVerificationInvalidation
            ? { confirmVerificationInvalidation: true }
            : {}),
        },
      });

      const error = data.value?.error || updateError.value;
      if (error) {
        if (
          useVerificationEntityEditGuard().isVerificationEditConfirmationError(
            error,
          )
        ) {
          throw error;
        }

        usePosthog().posthogLogError({
          message: "Failed to update gym",
          error,
          context: {
            userId: useUserStore().user?.id,
            action: "editGym",
          },
        });

        throw error;
      }

      const updatedGym = data.value?.data;

      if (updatedGym) {
        const index2 = this.gymsForUser.findIndex(
          (gym) => gym.id === updatedData.id,
        );
        if (index2 !== -1) {
          this.gymsForUser[index2] = updatedGym;
        }

        this.sortGymsByName();

        return updatedGym;
      }

      usePosthog().posthogLogError({
        message: "No data returned",
        error,
        context: {
          userId: useUserStore().user?.id,
          action: "editGym",
        },
      });

      throw new Error("Gym update failed: No data returned");
    },

    async deleteGym(gymId: string) {
      try {
        await $fetch(`/api/gyms`, {
          method: HttpMethod.DELETE,
          params: { id: gymId },
        });

        this.gymsForUser = this.gymsForUser.filter((gym) => gym.id !== gymId);
      } catch (error) {
        usePosthog().posthogLogError({
          message: "Failed to delete gym",
          error,
          context: {
            userId: useUserStore().user?.id,
            action: "deleteGym",
          },
        });
        throw error;
      }
    },

    async getGymById(
      gymId: string | null | undefined,
    ): Promise<GymWithMembers | undefined> {
      if (!gymId) {
        return undefined;
      }

      const gym = [...this.gyms, ...this.gymsForUser].find((gym) => {
        return gym.id == gymId;
      });

      if (
        gym &&
        gym.members !== undefined &&
        gym.fighters !== undefined &&
        gym.martial_arts !== undefined
      ) {
        return gym;
      }

      try {
        const { data, error } = await usePrivateApiFetch()(`/api/gyms`, {
          params: { id: gymId, members: true, fighters: true },
        });

        if (error) {
          throw error;
        }

        if (data && data.length > 0) {
          const gymData = data[0] as GymWithMembers;
          const existingGymIndex = this.gyms.findIndex(
            (gym) => gym.id === gymId,
          );

          if (existingGymIndex === -1) {
            this.gyms.push(gymData);
          } else {
            this.gyms[existingGymIndex] = gymData;
          }

          return gymData;
        }
      } catch (error) {
        usePosthog().posthogLogError({
          error,
          context: {
            userId: useUserStore().user?.id,
            action: "getGymById",
          },
        });

        throw error;
      }

      return undefined;
    },

    async getGymsForQuery(q: string | undefined): Promise<Tables<"gyms">[]> {
      if (q == undefined || q.length == 0) {
        return [];
      }

      const queryTrimmed = q.trim();

      const { data } = await useSupabaseClient()
        .from("gyms")
        .select("id,name,avatar_url")
        .or(`name.ilike.%${queryTrimmed}%`)
        .order("name", {
          ascending: true,
        })
        .limit(5);

      if (data != undefined) {
        return data;
      }

      return [];
    },

    async removeMemberFromGym(
      gymId: string,
      gymMemberId: string,
      leavingGym: boolean,
    ) {
      try {
        await $fetch(`/api/gyms/members`, {
          method: HttpMethod.DELETE,
          params: {
            id: gymId,
            member_id: gymMemberId,
          },
        });

        if (leavingGym) {
          this.gyms = this.gyms.filter((gym) => gym.id !== gymId);
          this.gymsForUser = this.gymsForUser.filter((gym) => gym.id !== gymId);
        } else {
          const index = this.gyms.findIndex((gym) => gym.id === gymId);

          this.gyms[index].members = this.gyms[index].members?.filter(
            (member) => member.user_id !== gymMemberId,
          );

          const index2 = this.gymsForUser.findIndex((gym) => gym.id === gymId);

          this.gymsForUser[index2].members = this.gymsForUser[
            index2
          ].members?.filter((member) => member.user_id !== gymMemberId);
        }
      } catch (error) {
        usePosthog().posthogLogError({
          message: "Failed to remove member from the gym",
          error,
          context: {
            userId: useUserStore().user?.id,
            action: "removeMemberFromGym",
          },
        });

        throw error;
      }
    },

    getUsersRoleForGym(gymId: string): OrganizationRole {
      const user = useUserStore().user;
      const gym = this.gymsForUser.find((gym) => gym.id === gymId);
      return (
        (gym?.members?.find((m) => m.user_id === user?.id)
          ?.role as OrganizationRole) ?? OrganizationRole.NO_ROLE
      );
    },

    sortGymsByName() {
      this.gyms.sort((a, b) => a.name.localeCompare(b.name));
      this.gymsForUser.sort((a, b) => a.name.localeCompare(b.name));
    },

    getGymMartialArtsString(gym: GymWithMembers) {
      const { t } = useI18n();

      return gym.martial_arts
        ?.map((art) => t("enum.martial_art." + art.toLowerCase()))
        .join(", ");
    },

    getGymFightCategoriesString(gym: GymWithMembers) {
      const { t } = useI18n();

      return gym.fight_categories
        ?.map((cat) => {
          if (cat == null || cat == OpenToFightCategories.Exhibition) {
            return null;
          }

          return t("enum.open_to_fight_category." + cat.toLowerCase());
        })
        .filter((cat) => cat != null)
        .join(", ");
    },

    onFighterAdded(data: PlayerUIModel) {
      this.gymsForUser
        .filter((gym) => gym.id == data.gym_id)
        .forEach((gym) => {
          if (!gym.fighters) {
            gym.fighters = [];
          }

          gym.fighters.push(data);
        });
    },

    onFighterUpdated(data: PlayerUIModel) {
      this.gymsForUser
        .filter((gym) => gym.id == data.gym_id)
        .forEach((gym) => {
          if (!gym.fighters) {
            return;
          }

          const indexGymFighters = gym.fighters.findIndex(
            (player) => player.id == data.id,
          );

          if (indexGymFighters != -1) {
            gym.fighters[indexGymFighters] = data;
          }
        });
    },

    onFighterDeleted(id: string) {
      this.gymsForUser.forEach((gym) => {
        if (!gym.fighters) {
          return;
        }

        gym.fighters = gym.fighters.filter((player) => player.id != id);
      });
    },
  },
});
